"use client"
import { Link } from "react-router-dom"
import styled from "styled-components"
import { motion } from "framer-motion"
import {
  Search as SearchIcon,
  Storage as StorageIcon,
  BarChart as BarChartIcon,
  Description as DescriptionIcon,
} from "@mui/icons-material"
import SynthlinxLogo from "../components/SynthlinxLogo"
import PageTransition from "../components/PageTransition"

const HomeContainer = styled(PageTransition)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 60px;
`

const HeroSection = styled(motion.div)`
  text-align: center;
  max-width: 800px;
  margin-top: 40px;
`

const LogoWrapper = styled(motion.div)`
  margin-bottom: 30px;
  display: flex;
  justify-content: center;
`

const Title = styled(motion.h1)`
  font-size: 3rem;
  margin-bottom: 20px;
  color: var(--primary-text);
  background: linear-gradient(135deg, var(--primary-text) 0%, var(--accent-blue) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  
  @media (max-width: 768px) {
    font-size: 2.2rem;
  }
`

const Subtitle = styled(motion.p)`
  font-size: 1.3rem;
  color: var(--secondary-text);
  margin-bottom: 40px;
  line-height: 1.6;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`

const GetStartedButton = styled(motion(Link))`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 16px 32px;
  background: linear-gradient(135deg, var(--accent-blue) 0%, var(--highlight) 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1.2rem;
  font-weight: 600;
  cursor: pointer;
  text-decoration: none;
  transition: all 0.3s ease;
  box-shadow: 0 8px 24px rgba(79, 141, 251, 0.3);
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 12px 28px rgba(79, 141, 251, 0.5);
  }
`

const FeaturesSection = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 30px;
  width: 100%;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`

const FeatureCard = styled(motion.div)`
  background-color: var(--input-bg);
  border-radius: 12px;
  padding: 30px;
  border: 1px solid var(--border);
  transition: all 0.3s ease;
  
  &:hover {
    border-color: var(--accent-blue);
    transform: translateY(-5px);
    box-shadow: 0 12px 28px rgba(0, 0, 0, 0.2);
  }
`

const FeatureIcon = styled(motion.div)`
  width: 60px;
  height: 60px;
  border-radius: 12px;
  background: linear-gradient(135deg, rgba(79, 141, 251, 0.1) 0%, rgba(108, 156, 255, 0.1) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
  color: var(--accent-blue);
  border: 1px solid rgba(79, 141, 251, 0.3);
`

const FeatureTitle = styled.h3`
  font-size: 1.4rem;
  margin-bottom: 16px;
  color: var(--primary-text);
`

const FeatureDescription = styled.p`
  color: var(--secondary-text);
  line-height: 1.6;
  font-size: 1.05rem;
`

const Home = ({ isAuthenticated }) => {
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        when: "beforeChildren",
        staggerChildren: 0.2,
      },
    },
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 25,
      },
    },
  }

  const logoVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 25,
      },
    },
  }

  const buttonVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 25,
        delay: 0.4,
      },
    },
    hover: {
      scale: 1.05,
      y: -5,
      boxShadow: "0 12px 28px rgba(79, 141, 251, 0.5)",
    },
    tap: { scale: 0.95 },
  }

  const featureCardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 25,
        delay: i * 0.1 + 0.3,
      },
    }),
    hover: {
      y: -10,
      boxShadow: "0 12px 28px rgba(0, 0, 0, 0.2)",
      borderColor: "var(--accent-blue)",
    },
  }

  const iconVariants = {
    hidden: { opacity: 0, scale: 0, rotate: -30 },
    visible: {
      opacity: 1,
      scale: 1,
      rotate: 0,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 25,
      },
    },
  }

  return (
    <HomeContainer>
      <HeroSection variants={containerVariants} initial="hidden" animate="visible">
        <LogoWrapper variants={logoVariants}>
          <SynthlinxLogo size="large" />
        </LogoWrapper>

        <Title variants={itemVariants}>Intelligent Logistics Query System</Title>

        <Subtitle variants={itemVariants}>
          Transform your logistics data into actionable insights with natural language queries. Synthlinx combines
          AI-powered analysis with beautiful visualizations to help you make better decisions, faster.
        </Subtitle>

        <GetStartedButton
          to={isAuthenticated ? "/query" : "/login"}
          variants={buttonVariants}
          whileHover="hover"
          whileTap="tap"
        >
          <SearchIcon />
          {isAuthenticated ? "Start Querying" : "Get Started"}
        </GetStartedButton>
      </HeroSection>

      <FeaturesSection variants={containerVariants} initial="hidden" animate="visible">
        <FeatureCard custom={0} variants={featureCardVariants} whileHover="hover">
          <FeatureIcon variants={iconVariants}>
            <SearchIcon fontSize="large" />
          </FeatureIcon>
          <FeatureTitle>Natural Language Queries</FeatureTitle>
          <FeatureDescription>
            Ask questions in plain English. Our advanced AI understands your intent and generates the appropriate
            queries to answer your logistics questions with precision and clarity.
          </FeatureDescription>
        </FeatureCard>

        <FeatureCard custom={1} variants={featureCardVariants} whileHover="hover">
          <FeatureIcon variants={iconVariants}>
            <StorageIcon fontSize="large" />
          </FeatureIcon>
          <FeatureTitle>Dual Processing Engine</FeatureTitle>
          <FeatureDescription>
            Synthlinx automatically generates both SQL queries and Python code to process your data, ensuring accurate
            and reliable results through our dual-verification system.
          </FeatureDescription>
        </FeatureCard>

        <FeatureCard custom={2} variants={featureCardVariants} whileHover="hover">
          <FeatureIcon variants={iconVariants}>
            <BarChartIcon fontSize="large" />
          </FeatureIcon>
          <FeatureTitle>Interactive Visualizations</FeatureTitle>
          <FeatureDescription>
            Get stunning visual representations of your logistics data with automatically generated charts and graphs
            that help you understand complex patterns and trends at a glance.
          </FeatureDescription>
        </FeatureCard>

        <FeatureCard custom={3} variants={featureCardVariants} whileHover="hover">
          <FeatureIcon variants={iconVariants}>
            <DescriptionIcon fontSize="large" />
          </FeatureIcon>
          <FeatureTitle>Comprehensive Reports</FeatureTitle>
          <FeatureDescription>
            Download professional DOCX reports that include your query, the generated code, results, visualizations, and
            detailed explanations to share with your team or stakeholders.
          </FeatureDescription>
        </FeatureCard>
      </FeaturesSection>
    </HomeContainer>
  )
}

export default Home